import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Building2, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

const IconBtn = ({ onClick, label, testid, children, danger }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`rounded-md p-1.5 transition-colors ${danger ? "text-rose-500 hover:bg-rose-50" : "text-slate-500 hover:bg-slate-100"}`}>{children}</button>
);

const parseLines = (text) => text.split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•·]|\d+[.)])\s*/, "").trim()).filter(Boolean);

const BulkAdd = ({ rooms, onChange, pi, onClose }) => {
  const [text, setText] = useState("");
  const lines = parseLines(text);
  const fresh = [...new Set(lines)].filter((l) => !rooms.includes(l));
  const add = () => {
    if (!fresh.length) { toast.error("No hay habitaciones nuevas para agregar"); return; }
    onChange([...rooms, ...fresh]);
    toast.success(`${fresh.length} habitaciones agregadas${lines.length > fresh.length ? ` · ${lines.length - fresh.length} repetidas omitidas` : ""}`);
    onClose();
  };
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-slate-300 bg-white p-3" data-testid={`room-bulk-${pi}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} autoFocus placeholder={"Pega una habitación por línea, por ejemplo:\nDeluxe – King Bed\nDeluxe – Two Double Beds"} data-testid={`room-bulk-text-${pi}`} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-500" data-testid={`room-bulk-count-${pi}`}>{fresh.length} nuevas{lines.length > fresh.length ? ` · ${lines.length - fresh.length} repetidas` : ""}</span>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid={`room-bulk-cancel-${pi}`}>Cancelar</Button>
          <Button type="button" size="sm" onClick={add} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid={`room-bulk-add-${pi}`}>Agregar todas</Button>
        </div>
      </div>
    </div>
  );
};

const RoomList = ({ rooms, onChange, pi }) => {
  const [draft, setDraft] = useState("");
  const [bulk, setBulk] = useState(false);
  const add = () => { const v = draft.trim(); if (!v) return; if (rooms.includes(v)) { toast.error("Esa habitación ya está en la lista"); return; } onChange([...rooms, v]); setDraft(""); };
  return (
    <div className="space-y-2">
      {rooms.map((r, i) => (
        <div key={i} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1" data-testid={`room-row-${pi}-${i}`}>
          <Input value={r} onChange={(e) => onChange(rooms.map((x, k) => (k === i ? e.target.value : x)))} className="h-8 border-0 shadow-none focus-visible:ring-0" data-testid={`room-input-${pi}-${i}`} />
          <IconBtn onClick={() => onChange(move(rooms, i, -1))} label="Subir"><ArrowUp size={14} /></IconBtn>
          <IconBtn onClick={() => onChange(move(rooms, i, 1))} label="Bajar"><ArrowDown size={14} /></IconBtn>
          <IconBtn onClick={() => onChange(rooms.filter((_, k) => k !== i))} label="Quitar habitación" testid={`room-remove-${pi}-${i}`} danger><Trash2 size={14} /></IconBtn>
        </div>
      ))}
      {!rooms.length && <p className="text-xs text-slate-400">Sin habitaciones: en Nuevo cobro se escribe como texto libre.</p>}
      <div className="flex flex-wrap gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Nueva habitación" className="h-9 min-w-[12rem] flex-1" data-testid={`room-new-input-${pi}`} />
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`room-add-btn-${pi}`}><Plus size={14} className="mr-1" />Agregar</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setBulk((b) => !b)} data-testid={`room-bulk-toggle-${pi}`}><ClipboardList size={14} className="mr-1" />Pegar lista</Button>
      </div>
      {bulk && <BulkAdd rooms={rooms} onChange={onChange} pi={pi} onClose={() => setBulk(false)} />}
    </div>
  );
};

export const PropertiesEditor = ({ hotel, onSaved, onDirtyChange }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setList((hotel.property_list || []).map((p) => ({ ...p, rooms: [...(p.rooms || [])] }))); setOpen(0); }, [hotel.slug, hotel.property_list]);
  const dirty = JSON.stringify(list.map((p) => ({ name: p.name, rooms: p.rooms }))) !== JSON.stringify((hotel.property_list || []).map((p) => ({ name: p.name, rooms: p.rooms || [] })));
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const update = (i, patch) => setList((l) => l.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  const addHotel = () => { setList((l) => [...l, { name: "", rooms: [] }]); setOpen(list.length); };
  const save = async () => {
    if (list.some((p) => !p.name.trim())) { toast.error("Escribe el nombre de cada hotel"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/hotels/${hotel.slug}/properties`, { properties: list });
      onSaved(data);
      toast.success("Hoteles y habitaciones guardados");
    } catch (e) { toast.error(apiError(e).message); } finally { setSaving(false); }
  };

  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="properties-editor">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Hoteles y habitaciones</h2>
          {dirty && <p className="mt-1 inline-flex rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200" data-testid="properties-unsaved-badge">Cambios sin guardar</p>}
          <p className="text-sm text-slate-500">Si agregas hoteles, en Nuevo cobro será obligatorio elegir uno. Las habitaciones de cada hotel aparecen como menú.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addHotel} data-testid="property-add-btn"><Plus size={16} className="mr-1" />Agregar hotel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2F5BFF] hover:bg-[#1F45E0]" data-testid="properties-save-btn"><Save size={16} className="mr-2" />{saving ? "Guardando…" : "Guardar"}</Button>
        </div>
      </div>
      {!list.length && <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid="properties-empty">Esta plantilla no tiene hoteles. Agrega uno para mostrar el menú "Hotel" en Nuevo cobro.</p>}
      <div className="space-y-3">
        {list.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60" data-testid={`property-row-${i}`}>
            <div className="flex flex-wrap items-center gap-2 p-3">
              <Building2 size={16} className="shrink-0 text-slate-400" />
              <Input value={p.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Nombre del hotel" className="h-9 min-w-[14rem] flex-1 bg-white font-medium" data-testid={`property-name-input-${i}`} />
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:ring-slate-400" data-testid={`property-toggle-${i}`}>
                {p.rooms.length} habitaciones
              </button>
              <IconBtn onClick={() => { setList((l) => move(l, i, -1)); setOpen(-1); }} label="Subir hotel"><ArrowUp size={14} /></IconBtn>
              <IconBtn onClick={() => { setList((l) => move(l, i, 1)); setOpen(-1); }} label="Bajar hotel"><ArrowDown size={14} /></IconBtn>
              <IconBtn onClick={() => { setList((l) => l.filter((_, k) => k !== i)); setOpen(-1); }} label="Quitar hotel" testid={`property-remove-${i}`} danger><Trash2 size={15} /></IconBtn>
            </div>
            {open === i && <div className="border-t border-slate-200 p-3"><RoomList rooms={p.rooms} pi={i} onChange={(rooms) => update(i, { rooms })} /></div>}
          </div>
        ))}
      </div>
    </section>
  );
};
